import { useState, useEffect } from 'react';
import { StoryType, STORY_TYPES } from '../../types/story';

interface StoryDetailsFormProps {
  storyType: StoryType;
  title: string;
  storyInput: string;
  onTitleChange: (title: string) => void;
  onStoryInputChange: (input: string) => void;
}

export default function StoryDetailsForm({
  storyType,
  title,
  storyInput,
  onTitleChange,
  onStoryInputChange,
}: StoryDetailsFormProps) {
  const [charCount, setCharCount] = useState(0);
  const maxChars = 2000;

  const selectedStoryType = STORY_TYPES.find((t) => t.value === storyType);

  useEffect(() => {
    setCharCount(storyInput.length);
  }, [storyInput]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 mb-4">
          <span className="text-5xl">{selectedStoryType?.emoji}</span>
          <h2 className="text-3xl font-display font-bold text-gray-900">
            {selectedStoryType?.label}
          </h2>
        </div>
        <p className="text-gray-600">
          Share the details of your child's story
        </p>
      </div>

      {/* Story Title */}
      <div>
        <label
          htmlFor="title"
          className="block text-sm font-semibold text-gray-900 mb-2"
        >
          Story Title
        </label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="input-field"
          placeholder={`e.g., "${selectedStoryType?.label} Adventure"`}
          maxLength={100}
        />
        <p className="text-xs text-gray-500 mt-1">
          Give your story a memorable name
        </p>
      </div>

      {/* Helpful Prompts */}
      <div className="p-4 bg-accent-50 border-2 border-accent-200 rounded-xl">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <span className="text-xl">💡</span>
          Helpful Prompts
        </h3>
        <ul className="space-y-2">
          {selectedStoryType?.prompts.map((prompt, index) => (
            <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
              <span className="text-accent-500 font-bold">•</span>
              <span>{prompt}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Story Input */}
      <div>
        <label
          htmlFor="story-input"
          className="block text-sm font-semibold text-gray-900 mb-2"
        >
          Your Story Details
        </label>
        <textarea
          id="story-input"
          value={storyInput}
          onChange={(e) => onStoryInputChange(e.target.value)}
          className="input-field min-h-[300px] resize-y"
          placeholder={`Share the details here...\n\nFor example:\n- Your child's name\n- Their feelings and concerns\n- Important context\n- What message you want to convey`}
          maxLength={maxChars}
        />

        {/* Character Counter */}
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-gray-500">
            Be as detailed as possible to create the best story
          </p>
          <p
            className={`text-xs font-semibold ${
              charCount > maxChars * 0.9
                ? 'text-red-500'
                : charCount > maxChars * 0.7
                ? 'text-yellow-600'
                : 'text-gray-500'
            }`}
          >
            {charCount} / {maxChars}
          </p>
        </div>

        {/* Character count warning */}
        {charCount > maxChars * 0.9 && (
          <div className="mt-2 p-3 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              You're approaching the character limit. Consider being more concise.
            </p>
          </div>
        )}
      </div>

      {/* Example Section */}
      <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <span className="text-xl">📖</span>
          Example
        </h3>
        <p className="text-sm text-blue-800 leading-relaxed">
          "My daughter Emma (age 6) is starting first grade at Sunnydale Elementary next
          week. She's excited but also nervous about making new friends. Her best friend
          from kindergarten is going to a different school. Emma loves drawing and animals.
          I want her to feel confident and know that it's okay to be nervous about new
          things."
        </p>
      </div>
    </div>
  );
}
